import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  adminBooks, adminAuthors, adminCategories, adminCreateBook, adminUpdateBook,
  adminDeleteBook, adminDuplicateBook, adminUpload, apiError,
} from "@/lib/api";
import { AdminHeader, PrimaryBtn, Modal, fieldCls, labelCls, ImageHint } from "./ui";
import { BookSaleSection } from "./BookSaleSection";

const EMPTY = {
  titulo: "", subtitulo: "", autor_id: "", categoria_id: "", portada: "",
  sinopsis_corta: "", sinopsis_completa: "", idioma: "Español", isbn: "",
  fecha_publicacion: "", formato: "Impreso", ebook_disponible: false,
  descarga_gratuita: false, archivo_descarga: "", destacado_home: false,
  nuevo_lanzamiento: false, estado: "publicado", seo_title: "", seo_description: "",
  purchase_links: [],
  venta_directa: false, precio_eur: 0, precio_usd: 0, epub_venta_uid: "", epub_venta_nombre: "", epub_venta_size: 0,
};

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docBusy, setDocBusy] = useState(false);

  const load = () => adminBooks().then(setBooks);
  useEffect(() => {
    load();
    adminAuthors().then(setAuthors);
    adminCategories().then(setCats);
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b, purchase_links: (b.purchase_links || []).map((l) => ({ _key: crypto.randomUUID(), ...l })) }); setOpen(true); };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload(file);
      setForm((f) => ({ ...f, portada: res.url }));
      toast.success("Portada subida.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const addLink = () => setForm({ ...form, purchase_links: [...form.purchase_links, { _key: crypto.randomUUID(), pais_o_region: "", nombre_tienda: "", url: "", tipo: "impreso" }] });
  const setLink = (i, k, v) => {
    const links = [...form.purchase_links];
    links[i] = { ...links[i], [k]: v };
    setForm({ ...form, purchase_links: links });
  };
  const removeLink = (i) => setForm({ ...form, purchase_links: form.purchase_links.filter((_, x) => x !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, precio_eur: Number(form.precio_eur) || 0, precio_usd: Number(form.precio_usd) || 0, purchase_links: form.purchase_links.map(({ _key, ...rest }) => rest) };
    if (payload.venta_directa && !payload.epub_venta_uid) { setSaving(false); return toast.error("Sube el archivo EPUB para activar la venta directa."); }
    if (payload.venta_directa && !payload.precio_eur) { setSaving(false); return toast.error("Indica el precio en euros."); }
    try {
      if (editing) { await adminUpdateBook(editing.id, payload); toast.success("Libro actualizado."); }
      else { await adminCreateBook(payload); toast.success("Libro creado."); }
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar "${b.titulo}"?`)) return;
    await adminDeleteBook(b.id); toast.success("Eliminado."); load();
  };
  const duplicate = async (b) => { await adminDuplicateBook(b.id); toast.success("Duplicado."); load(); };

  return (
    <div>
      <AdminHeader title="Libros" subtitle={`${books.length} libro(s)`} action={<PrimaryBtn onClick={openNew} testId="new-book-btn"><Plus size={15} /> Nuevo libro</PrimaryBtn>} />

      <div className="rounded-xl bg-white border border-[#0f2544]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#eceae4] text-left text-[#8a8a83]">
            <tr>
              <th className="px-4 py-3 font-medium">Portada</th>
              <th className="px-4 py-3 font-medium">Título</th>
              <th className="px-4 py-3 font-medium">Autor</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#0f2544]/6">
            {books.map((b) => (
              <tr key={b.id} data-testid={`book-row-${b.slug}`}>
                <td className="px-4 py-3"><div className="w-9 h-12 bg-[#e7e5df] rounded overflow-hidden">{b.portada && <img src={b.portada} alt="" className="w-full h-full object-cover" />}</div></td>
                <td className="px-4 py-3 font-medium text-[#0f2544]">{b.titulo}</td>
                <td className="px-4 py-3 text-[#8a8a83]">{b.autor_nombre}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${b.estado === "publicado" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{b.estado}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(b)} className="p-2 text-[#8a8a83] hover:text-[#1f44e0]" data-testid={`edit-book-${b.slug}`}><Pencil size={15} /></button>
                    <button onClick={() => duplicate(b)} className="p-2 text-[#8a8a83] hover:text-[#1f44e0]" title="Duplicar"><Copy size={15} /></button>
                    <button onClick={() => remove(b)} className="p-2 text-[#8a8a83] hover:text-red-600" data-testid={`delete-book-${b.slug}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar libro" : "Nuevo libro"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="book-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Título *</label><input required value={form.titulo} onChange={set("titulo")} className={fieldCls} data-testid="book-titulo" /></div>
            <div><label className={labelCls}>Subtítulo</label><input value={form.subtitulo} onChange={set("subtitulo")} className={fieldCls} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Autor</label>
              <select value={form.autor_id} onChange={set("autor_id")} className={fieldCls} data-testid="book-autor">
                <option value="">—</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Categoría</label>
              <select value={form.categoria_id} onChange={set("categoria_id")} className={fieldCls} data-testid="book-categoria">
                <option value="">—</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Portada</label>
            <ImageHint>Medida recomendada: <strong>800 × 1200 px</strong> (vertical, relación 2:3). Formato JPG o PNG.</ImageHint>
            <div className="flex items-center gap-3">
              {form.portada && <img src={form.portada} alt="" className="w-12 h-16 object-cover rounded border border-[#0f2544]/10" />}
              <input type="file" accept="image/*" onChange={upload} className="text-sm" data-testid="book-cover-upload" />
              {uploading && <span className="text-xs text-[#8a8a83]">Subiendo...</span>}
            </div>
            <input value={form.portada} onChange={set("portada")} className={`${fieldCls} mt-2`} placeholder="o pega una URL de portada" />
            <p className="text-xs text-[#8a8a83] mt-1">Si pegas un enlace, al guardar copiamos la imagen a nuestro almacenamiento: la web no dependerá del enlace externo.</p>
          </div>
          <div><label className={labelCls}>Sinopsis corta</label><textarea rows={2} value={form.sinopsis_corta} onChange={set("sinopsis_corta")} className={fieldCls} /></div>
          <div><label className={labelCls}>Sinopsis completa</label><textarea rows={4} value={form.sinopsis_completa} onChange={set("sinopsis_completa")} className={fieldCls} /></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className={labelCls}>Idioma</label><input value={form.idioma} onChange={set("idioma")} className={fieldCls} /></div>
            <div><label className={labelCls}>ISBN</label><input value={form.isbn} onChange={set("isbn")} className={fieldCls} /></div>
            <div><label className={labelCls}>Formato</label><input value={form.formato} onChange={set("formato")} className={fieldCls} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Fecha de publicación</label><input value={form.fecha_publicacion} onChange={set("fecha_publicacion")} className={fieldCls} placeholder="Ej: 2024" /></div>
            <div>
              <label className={labelCls}>Estado</label>
              <select value={form.estado} onChange={set("estado")} className={fieldCls}>
                <option value="publicado">Publicado</option>
                <option value="borrador">Borrador</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-5 py-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.destacado_home} onChange={set("destacado_home")} className="accent-[#1f44e0]" data-testid="book-destacado" /> Destacar en inicio</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.nuevo_lanzamiento} onChange={set("nuevo_lanzamiento")} className="accent-[#1f44e0]" /> Nuevo lanzamiento</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.ebook_disponible} onChange={set("ebook_disponible")} className="accent-[#1f44e0]" /> Ebook disponible</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.descarga_gratuita} onChange={set("descarga_gratuita")} className="accent-[#1f44e0]" /> Descarga gratuita</label>
          </div>
          {form.descarga_gratuita && (
            <div><label className={labelCls}>URL de descarga gratuita</label><input value={form.archivo_descarga} onChange={set("archivo_descarga")} className={fieldCls} /></div>
          )}

          <div className="border-t border-[#0f2544]/8 pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls}>Enlaces de compra</label>
              <button type="button" onClick={addLink} className="text-xs text-[#1f44e0] font-semibold" data-testid="add-purchase-link">+ Añadir enlace</button>
            </div>
            <div className="space-y-2">
              {form.purchase_links.map((l, i) => (
                <div key={l._key} className="grid grid-cols-[1fr_1fr_0.7fr_auto] gap-2 items-center">
                  <input value={l.pais_o_region} onChange={(e) => setLink(i, "pais_o_region", e.target.value)} placeholder="País/Región" className={fieldCls} />
                  <input value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="URL" className={fieldCls} />
                  <select value={l.tipo} onChange={(e) => setLink(i, "tipo", e.target.value)} className={fieldCls}>
                    <option value="impreso">Impreso</option>
                    <option value="ebook">Ebook</option>
                  </select>
                  <button type="button" onClick={() => removeLink(i)} className="text-red-500 px-2"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <BookSaleSection form={form} setForm={setForm} onBusy={setDocBusy} />

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 text-sm text-[#8a8a83]">Cancelar</button>
            <PrimaryBtn type="submit" disabled={saving || docBusy} testId="save-book-btn">{saving ? "Guardando..." : docBusy ? "Subiendo archivo..." : "Guardar"}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
